import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Popover
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { alpha } from "@mui/material/styles";
import { ArrowDropDown, ArrowDropUp, Clear, DoneAll } from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { FS } from "@plannel/utils/font-util";

const REPLENISHMENT_QTY = "zeroReplenishment";
const PURCHASE_QTY = "zeroPurchase";
const DEMAND_QTY = "zeroDemand";
const EOH_QTY = "zeroEoh";
const PROD_QTY = "zeroProd";

const DEFAULT_EXCEPTION_CODE = [];
const EXCEPTION_LIST = [
  REPLENISHMENT_QTY,
  PURCHASE_QTY,
  DEMAND_QTY,
  EOH_QTY
];
const EXCEPTION_LABELS = {
  [REPLENISHMENT_QTY]: "zeroReplenishment",
  [PURCHASE_QTY]: "zeroPurchase",
  [DEMAND_QTY]: "zeroDemand",
  [EOH_QTY]: "zeroEoh",
  [PROD_QTY]: "zeroProd"
};

const ZeroExclusionFilter = ({ t, exceptionList = EXCEPTION_LIST, appliedExceptions, setAppliedExceptions, disabled = false, isLayout = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(disabled ? null : anchorEl);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedAppliedExceptions, setSelectedAppliedExceptions] = useState([]);
  const [state, setState] = useState({
    [REPLENISHMENT_QTY]: false,
    [PURCHASE_QTY]: false,
    [DEMAND_QTY]: false,
    [EOH_QTY]: false
  });
  const inputRef = useRef();

  const handleClick = (event) => {
    inputRef.current.focus();
    setAnchorEl(event.currentTarget);
  };

  const handleMouseEnter = () => {
    setIsFocused(true);
  };

  const handleMouseLeave = () => {
    setIsFocused(false);
  };

  const handleClose = () => {
    setAppliedExceptions(selectedAppliedExceptions);
    setAnchorEl(null);
  };

  const onClearHandler = () => {
    setAppliedExceptions(DEFAULT_EXCEPTION_CODE);
    setSelectedAppliedExceptions(DEFAULT_EXCEPTION_CODE);
  };

  const onCheckBoxHandler = (event) => {
    const name = event.target.value;
    const code = String(name);
    const checkedValue = event.target.checked;

    if (checkedValue) {
      const addAndSort = [...selectedAppliedExceptions, code];
      addAndSort.sort((a, b) => a.localeCompare(b));
      console.log(addAndSort);

      setSelectedAppliedExceptions(addAndSort);
    } else {
      const filtered = selectedAppliedExceptions.filter(value => value !== code);
      setSelectedAppliedExceptions(filtered);
    }
  };

  const isCheckValue = (value) => {
    return selectedAppliedExceptions.includes(value);
  };

  const handleChange = (event) => {
    const value = { ...state, [event.target.name]: event.target.checked };
    setState(value);
    setAppliedExceptions(Object.keys(value).filter(key => value[key]));
  };

  useEffect(() => {
    if (!isLayout) {
      setSelectedAppliedExceptions(isEmpty(appliedExceptions) ? DEFAULT_EXCEPTION_CODE : appliedExceptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedExceptions]);

  useEffect(() => {
    if (isLayout) {
      const obj = {};
      for (const code of EXCEPTION_LIST) {
        obj[code] = appliedExceptions.includes(code);
      }
      setState(obj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    isLayout ? (
      <FormControl
        className={"zero-exclusion-filter"}
        component="fieldset"
        size="small"
        sx={{ mr: 1, minWidth: 90, width: 200, height: 200 }}
      >
        <FormGroup>
          {exceptionList.map((code) => (
            <FormControlLabel
              key={code}
              control={
                <Checkbox checked={state[code]} onChange={handleChange} name={code} />
              }
              label={t(code)}
            />
          ))}
        </FormGroup>
      </FormControl>
    ) : (
      <Box>
        <FormControl
          className={"exceptions-filter"}
          size="small"
          sx={{ mr: 1, minWidth: 250, width: 250 }}
        >
          <TextField
            size="small"
            label={t("excludeCondition")}
            type="string"
            disabled={disabled}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            value={
              appliedExceptions && appliedExceptions.length > 0
                ? appliedExceptions.map(code => t(EXCEPTION_LABELS[code])).join(', ')
                : ''
            }
            inputRef={inputRef}
            autoComplete="off"
            InputProps={{
              endAdornment: appliedExceptions?.length > 0 ? (
                <>
                  {isFocused ? (
                    <IconButton size="small" aria-label="clear text" onClick={e => {
                      e.stopPropagation();
                      onClearHandler();
                    }}>
                      <Clear sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  ) : null}
                  <InputAdornment position="end">
                    <IconButton disabled={disabled} aria-label="search" sx={{ padding: 0 }} size="small">{open ? <ArrowDropUp /> : <ArrowDropDown />}</IconButton>
                  </InputAdornment>
                </>
              ) : (
                <InputAdornment position="end">
                  <IconButton disabled={disabled} aria-label="search" size="small">{open ? <ArrowDropUp /> : <ArrowDropDown />}</IconButton>
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <Popover
          open={Boolean(open)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disableAutoFocus
          disableEnforceFocus
          TransitionProps={{ timeout: 0 }}
        >
          <Box>
            <Box sx={{ minWidth: 250, width: 250, overflowY: "auto", overflowX: "hidden" }}>
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                {/* 행 스타일: 드롭다운 공통 룩(둥근 호버·선택 틴트·13px)과 통일 */}
                <FormGroup sx={{ p: "4px 6px" }}>
                  {exceptionList.map((code) => (
                    <FormControlLabel
                      sx={{
                        m: 0, px: 0.5, borderRadius: 1.5,
                        bgcolor: (theme) => isCheckValue(code) ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                        "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, isCheckValue(code) ? 0.16 : 0.06) },
                        "& .MuiFormControlLabel-label": { fontSize: FS.TITLE },
                      }}
                      checked={isCheckValue(code)}
                      value={code}
                      control={<Checkbox size="small" sx={{ p: 0.75 }} />}
                      label={t(EXCEPTION_LABELS[code])}
                      onChange={onCheckBoxHandler}
                      key={code}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
            <Box>
              <Divider sx={{ backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 0.5, opacity: 0.2 }} />
              <Box sx={{ padding: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<DoneAll />}
                  size="small"
                  color="primary"
                  onClick={handleClose}
                >
                  {t("confirm")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
    )
  );
};

export default withTranslation()(ZeroExclusionFilter);
